// frontend/src/components/MapViewImpact.jsx

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import mapboxgl from 'mapbox-gl';
import { icons } from "./icons";
import { 
    Fab, Box, Paper, Typography, ToggleButtonGroup, 
    ToggleButton, Switch, FormControlLabel 
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import API_BASE_URL from '../config';

// --- Legend Component ---
const MapLegend = ({ getSeverityColor, displayMetric, isTransport }) => {
    const theme = useTheme();

    let title = 'Criticality';
    let severityLevels =[];

    // Customize legend labels based on the currently displayed metric
    if (!isTransport || displayMetric === 'flow') {
        title = isTransport ? 'Flow Volume' : 'Criticality';
        severityLevels =[
            { level: 'extreme', label: '> 10,000 (Extreme)' },
            { level: 'severe', label: '2,000 - 10k (Severe)' },
            { level: 'high', label: '500 - 2,000 (High)' },
            { level: 'medium', label: '100 - 500 (Medium)' },
            { level: 'low', label: '< 100 (Low)' },
        ];
    } else if (displayMetric === 'vc_ratio') {
        title = 'Congestion (V/C)';
        severityLevels =[
            { level: 'extreme', label: 'V/C > 1.5 (Extreme)' },
            { level: 'severe', label: '1.2 - 1.5 (Severe)' },
            { level: 'high', label: '1.0 - 1.2 (High)' },
            { level: 'medium', label: '0.85 - 1.0 (Medium)' },
            { level: 'low', label: '< 0.85 (Low)' },
        ];
    } else if (displayMetric === 'delay_min') {
        title = 'Travel Delay';
        severityLevels =[
            { level: 'extreme', label: '> 30 min (Extreme)' },
            { level: 'severe', label: '15 - 30 min (Severe)' },
            { level: 'high', label: '5 - 15 min (High)' },
            { level: 'medium', label: '2 - 5 min (Medium)' },
            { level: 'low', label: '< 2 min (Low)' },
        ];
    }

    return (
        <Paper
            sx={{
                position: 'absolute',
                bottom: 16,
                left: 16,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(33, 33, 33, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                padding: '12px',
                borderRadius: '8px',
                zIndex: 1,
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            }}
        >
            <Typography variant="subtitle2" sx={{ marginBottom: '5px', fontWeight: 'bold' }}>{title}</Typography>
            {severityLevels.map(({ level, label }) => (
                <Box key={level} sx={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                    <Box
                        sx={{
                            width: 20,
                            height: 10,
                            backgroundColor: getSeverityColor(level),
                            marginRight: '10px',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '2px',
                        }}
                    />
                    <Typography variant="body2">{label}</Typography>
                </Box>
            ))}
        </Paper>
    );
};

const MapViewImpact = ({ system = 'water', selectedFeature = null, floodData, scenarioId = null }) => {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const popup = useRef(null);
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const[isStyleReady, setIsStyleReady] = useState(false);
    const setupInProgress = useRef(false);
    const theme = useTheme();
    
    // Track if the component is mounted to prevent state updates on unmount
    const isMounted = useRef(true);

    // New Controls State
    const isTransport = !['power', 'water'].includes(system);
    const [displayMetric, setDisplayMetric] = useState('flow'); // 'flow', 'vc_ratio', 'delay_min'
    const [showHazard, setShowHazard] = useState(true);

    // State Refs to ensure callbacks have latest values without re-mounting Mapbox layers
    const displayMetricRef = useRef(displayMetric);
    const showHazardRef = useRef(showHazard);

    useEffect(() => { displayMetricRef.current = displayMetric; }, [displayMetric]);
    useEffect(() => { showHazardRef.current = showHazard; },[showHazard]);

    const getTileUrl = useCallback((sys) => {
        if (scenarioId) {
            const scenarioPath = `scenarios/${scenarioId}/scenario_analysis_${sys}`;
            return `${API_BASE_URL}/api/tiles/${scenarioPath}/{z}/{x}/{y}.pbf`;
        }
        return `${API_BASE_URL}/api/tiles/realtime_analysis_${sys}/{z}/{x}/{y}.pbf`;
    }, [scenarioId]);

    const getSourceLayerName = useCallback((sys) => {
        if (scenarioId) {
            return `scenario_analysis_output_${sys}`;
        }
        return `realtime_analysis_${sys}`;
    }, [scenarioId]);

    const getSeverityColor = useCallback((severity) => {
        switch (severity?.toLowerCase()) {
            case 'extreme': return '#FFFF00'; // Yellow
            case 'severe': return '#FFA500';  // Orange
            case 'high': return '#FF0000';    // Red
            case 'medium': return '#8B0000';  // Dark Red
            case 'low': return '#800080';     // Purple
            default: return theme.palette.mode === 'dark' ? '#555' : '#D3D3D3';
        }
    }, [theme.palette.mode]);

    const getFloodRiskColor = useCallback((risk_level) => {
        switch (risk_level?.toLowerCase()) {
            case 'high': return '#0D47A1';
            case 'moderate': return '#1976D2';
            case 'low': return '#64B5F6';
            default: return '#BBDEFB';
        }
    },[]);

    const addIconToMap = useCallback((url, id, color = null) => {
        return new Promise((resolve, reject) => {
            if (!map.current) {
                return reject(new Error(`Map not initialized for icon: ${id}`));
            }
            
            const img = new Image();
            img.crossOrigin = "Anonymous";
            img.src = url;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
                canvas.width = img.width || 24;
                canvas.height = img.height || 24;
                context.drawImage(img, 0, 0, canvas.width, canvas.height);

                if (color) {
                    context.globalCompositeOperation = 'source-atop';
                    context.fillStyle = color;
                    context.fillRect(0, 0, canvas.width, canvas.height);
                }

                const imageData = context.getImageData(0, 0, canvas.width, canvas.height);

                try {
                    if (!isMounted.current || !map.current) return reject(new Error('Component unmounted'));
                    if (map.current.hasImage(id)) map.current.removeImage(id);
                    map.current.addImage(id, imageData);
                    resolve();
                } catch (error) {
                    reject(new Error(`Failed to add image ${id} to map: ${error.message}`));
                }
            };
            img.onerror = (error) => reject(new Error(`Failed to load image for icon ${id}: ${error}`));
        });
    },[]);

    const classifyValue = useCallback((value) => {
        if (value === undefined || value === null || value <= 0) return 'none';
        if (value <= 1e-6) return 'none';
        if (value <= 100) return 'low';
        if (value <= 500) return 'medium';
        if (value <= 2000) return 'high';
        if (value <= 10000) return 'severe';
        return 'extreme';
    },[]);

    // Resolves proper paint properties dynamically depending on display metric
    const getPaintExpressions = useCallback((metric, sys) => {
        const prop = `${metric}_${sys}`;
        let colorExp, opacityExp;

        if (metric === 'flow') {
            colorExp = ['step', ['get', prop],
                getSeverityColor('low'), 1,
                getSeverityColor('low'), 101,
                getSeverityColor('medium'), 501,
                getSeverityColor('high'), 2001,
                getSeverityColor('severe'), 10001,
                getSeverityColor('extreme')
            ];
        } else if (metric === 'vc_ratio') {
            colorExp = ['step', ['get', prop],
                getSeverityColor('low'), 0.85,
                getSeverityColor('medium'), 1.0,
                getSeverityColor('high'), 1.2,
                getSeverityColor('severe'), 1.5,
                getSeverityColor('extreme')
            ];
        } else if (metric === 'delay_min') {
            colorExp = ['step', ['get', prop],
                getSeverityColor('low'), 2.0,
                getSeverityColor('medium'), 5.0,
                getSeverityColor('high'), 15.0,
                getSeverityColor('severe'), 30.0,
                getSeverityColor('extreme')
            ];
        }

        opacityExp = ['case', ['>',['to-number', ['get', prop]], 0], 1.0, 0.0];
        return { colorExp, opacityExp };
    }, [getSeverityColor]);

    const createFloodPopupContent = useCallback((properties) => {
        return `<div class="popup-container"><div class="popup-body"><p class="popup-detail"><strong>Hazard Area</strong></p></div></div>`;
    },[]);

    const createImpactPopupContent = useCallback((properties) => {
        const code = properties.code || properties.node_id;
        const name = properties.name;
        const type = properties.type;
        const systemLabel = system.charAt(0).toUpperCase() + system.slice(1);
        
        const metricPropFromTile = isTransport ? `restricted_${system}` : `unmet_${system}`;
        const metricPropFromClick = isTransport ? `absolute_restricted` : `absolute_unmet`;
        const metricValue = properties[metricPropFromTile] ?? properties[metricPropFromClick];
        const metricLabel = isTransport ? `Restricted Supply` : `Unmet Demand`;
        
        const flowProp = `flow_${system}`;
        const vcProp = `vc_ratio_${system}`;
        const delayProp = `delay_min_${system}`;

        const flowValue = properties[flowProp];
        const vcValue = properties[vcProp];
        const delayValue = properties[delayProp];

        const severity = properties.severity || classifyValue(flowValue !== undefined && flowValue > 0 ? flowValue : metricValue);

        let content = `<div class="popup-container"><div class="popup-header"><strong style="color: #000;">${code || name || type || 'Unknown Feature'}</strong></div><div class="popup-body">`;
        if (type) content += `<p><strong>Type:</strong> ${type}</p>`;
        
        // Node metric
        if (metricValue !== undefined && metricValue > 0) {
            content += `<p><strong>${metricLabel}:</strong> ${parseInt(metricValue, 10)?.toLocaleString() ?? 'N/A'}</p>`;
        }
        
        // Edge metrics
        if (flowValue !== undefined && flowValue > 0) {
            content += `<p><strong>${systemLabel} Flow:</strong> ${parseInt(flowValue, 10)?.toLocaleString() ?? 'N/A'}</p>`;
        }
        if (vcValue !== undefined && vcValue > 0) {
            content += `<p><strong>Congestion (V/C):</strong> ${Number(vcValue).toFixed(2)}</p>`;
        }
        if (delayValue !== undefined && delayValue > 0) {
            content += `<p><strong>Travel Delay:</strong> ${Number(delayValue).toFixed(1)} min</p>`;
        }

        content += `<p><strong>Criticality:</strong> ${severity !== 'none' ? severity.charAt(0).toUpperCase() + severity.slice(1) : 'None'}</p></div></div>`;
        return content;
    }, [system, isTransport, classifyValue]);

    const addPopupEventListeners = useCallback(() => {
        if (!map.current) return;
        if (!popup.current) {
            popup.current = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, className: 'custom-popup' });
        }

        const handleImpactMouseEnter = (e) => {
            if (!map.current || !e.features || e.features.length === 0) return;
            const feature = e.features[0];
            if (feature.properties?.type === 'Road') return;
            map.current.getCanvas().style.cursor = 'pointer';
            popup.current.setLngLat(e.lngLat).setHTML(createImpactPopupContent(feature.properties)).addTo(map.current);
        };

        const handleFloodMouseEnter = (e) => {
            if (!map.current || !e.features || e.features.length === 0) return;
            const feature = e.features[0];
            map.current.getCanvas().style.cursor = 'pointer';
            popup.current.setLngLat(e.lngLat).setHTML(createFloodPopupContent(feature.properties)).addTo(map.current);
        };

        const handleMouseLeave = () => {
            if (!map.current) return;
            map.current.getCanvas().style.cursor = '';
            if (popup.current) popup.current.remove();
        };

        const impactLayers = ['impact-nodes', 'impact-lines'];
        impactLayers.forEach(layerId => {
            if (map.current.getLayer(layerId)) {
                map.current.off('mouseenter', layerId, handleImpactMouseEnter);
                map.current.on('mouseenter', layerId, handleImpactMouseEnter);
                map.current.on('mouseleave', layerId, handleMouseLeave);
            }
        });

        const floodLayers =['flood-areas', 'hazard-fill'];
        floodLayers.forEach(layerId => {
            if (map.current.getLayer(layerId)) {
                map.current.off('mouseenter', layerId, handleFloodMouseEnter);
                map.current.on('mouseenter', layerId, handleFloodMouseEnter);
                map.current.on('mouseleave', layerId, handleMouseLeave);
            }
        });

    },[createImpactPopupContent, createFloodPopupContent]);

    const addLayers = useCallback(() => {
        if (!map.current) return;
        try {
            const layersToRemove =['impact-nodes', 'context-nodes', 'impact-lines', 'base-lines', 'flood-areas', 'hazard-fill', 'hazard-outline'];
            layersToRemove.forEach(id => {
                if (map.current.getLayer(id)) map.current.removeLayer(id);
            });
            
            if (map.current.getSource('network-tiles')) map.current.removeSource('network-tiles');
            if (map.current.getSource('flood-data')) map.current.removeSource('flood-data');
            if (map.current.getSource('hazard-data')) map.current.removeSource('hazard-data');

            const tileUrl = getTileUrl(system);
            const sourceLayerName = getSourceLayerName(system);
            
            map.current.addSource('network-tiles', { type: 'vector', tiles: [tileUrl], minzoom: 0, maxzoom: 12 });

            const hazardVisibility = showHazardRef.current ? 'visible' : 'none';

            // 1. Hazard Vector Tiles
            if (scenarioId) {
                map.current.addSource('hazard-data', {
                    type: 'vector',
                    tiles:[`${API_BASE_URL}/api/tiles/scenarios/${scenarioId}/hazard_area/{z}/{x}/{y}.pbf`],
                    minzoom: 0,
                    maxzoom: 12
                });
                
                const HAZARD_SOURCE_LAYER = 'hazard_area';
                map.current.addLayer({
                    id: 'hazard-fill',
                    type: 'fill',
                    source: 'hazard-data',
                    'source-layer': HAZARD_SOURCE_LAYER,
                    layout: { visibility: hazardVisibility },
                    paint: {
                        'fill-color': theme.palette.mode === 'dark' ? '#ff4d4d' : '#ff6666',
                        'fill-opacity': 0.4
                    }
                });
                map.current.addLayer({
                    id: 'hazard-outline',
                    type: 'line',
                    source: 'hazard-data',
                    'source-layer': HAZARD_SOURCE_LAYER,
                    layout: { visibility: hazardVisibility },
                    paint: {
                        'line-color': theme.palette.mode === 'dark' ? '#ff8080' : '#e60000',
                        'line-width': 1.5
                    }
                });

            // 2. Fallback Hazard GeoJSON
            } else if (floodData && floodData.features && floodData.features.length > 0) {
                map.current.addSource('flood-data', { type: 'geojson', data: floodData });
                map.current.addLayer({
                    id: 'flood-areas',
                    type: 'fill',
                    source: 'flood-data',
                    layout: { visibility: hazardVisibility },
                    paint: {
                        'fill-color': ['match',['get', 'risk_level'], 'High', getFloodRiskColor('high'), 'Moderate', getFloodRiskColor('moderate'), 'Low', getFloodRiskColor('low'), getFloodRiskColor('default')],
                        'fill-opacity': 0.5
                    }
                });
            }

            // Edges setup
            map.current.addLayer({
                id: 'base-lines',
                type: 'line',
                source: 'network-tiles',
                'source-layer': sourceLayerName,
                filter:['==', '$type', 'LineString'],
                paint: { 'line-color': theme.palette.mode === 'dark' ? '#444' : '#E0E0E0', 'line-width': 0.8 }
            });

            const activeMetric = isTransport ? displayMetricRef.current : 'flow';
            const { colorExp, opacityExp } = getPaintExpressions(activeMetric, system);

            map.current.addLayer({
                id: 'impact-lines',
                type: 'line',
                source: 'network-tiles',
                'source-layer': sourceLayerName,
                filter:['==', '$type', 'LineString'],
                layout: { 'line-join': 'round', 'line-cap': 'round' },
                paint: {
                    'line-color': colorExp,
                    'line-width': 2.5,
                    'line-opacity': opacityExp
                }
            });

            // Nodes Setup
            const unmetProp = `unmet_${system}`;
            const unmetSevProp = `unmet_sev_${system}`;
            const restrictedProp = `restricted_${system}`;
            const restrictedSevProp = `restricted_sev_${system}`;
            const getIconName = (baseName) => theme.palette.mode === 'dark' ? `${baseName}-dark` : baseName;

            map.current.addLayer({
                id: 'context-nodes',
                type: 'symbol',
                source: 'network-tiles',
                'source-layer': sourceLayerName,
                filter: ['all', 
                    ['==', '$type', 'Point'], 
                    ['!=', 'type', 'road'], 
                    ['!=', 'name', ''], 
                    ['!=', 'type', ''], 
                    ['all',
                        ['any',['!has', unmetProp], ['<=', unmetProp, 0]],
                        ['any',['!has', restrictedProp], ['<=', restrictedProp, 0]]
                    ]
                ],
                layout: { 
                    'icon-image': ['match', ['get', 'type'], 
                        'School', getIconName('school'), 
                        'Port', getIconName('directions-boat'), 
                        'Harbour', getIconName('directions-boat'), 
                        'Household', getIconName('home'), 
                        'Supermarket', getIconName('local-grocery-store'), 
                        'Warehouse', getIconName('warehouse'), 
                        'Farm', getIconName('agriculture'), 
                        'Power station', getIconName('factory'), 
                        'Power substation', getIconName('power'), 
                        'Hospital', getIconName('local-hospital'), 
                        'Clinic', getIconName('local-hospital'), 
                        'Pharmacy', getIconName('pharmacy'), 
                        'Fire station', getIconName('fire-station'), 
                        'Police station', getIconName('police'), 
                        'Military base', getIconName('police'), 
                        'Train station', getIconName('train'), 
                        'Airport', getIconName('plane'), 
                        'Government office', getIconName('office'), 
                        'Courthouse', getIconName('office'), 
                        'City hall', getIconName('office'), 
                        'Communication tower', getIconName('communication-tower'), 
                        'Water reservoir', getIconName('reservoir'), 
                        'Water plant', getIconName('droplet'), 
                        'Wastewater plant', getIconName('droplet'), 
                        getIconName('home') // Fallback
                    ],
                    'icon-size': 0.8, 
                    'icon-allow-overlap': true 
                },
                paint: { 'icon-opacity': 0.35 },
                minzoom: 12
            });

            const iconMatchList =["Road", "road", "road", "road",'School', 'school', 'Port', 'directions-boat', 'Harbour', 'directions-boat', 'Household', 'home', 'Supermarket', 'local-grocery-store', 'Warehouse', 'warehouse', 'Farm', 'agriculture', 'Power station', 'factory', 'Power substation', 'power', 'Hospital', 'local-hospital', 'Clinic', 'local-hospital', 'Pharmacy', 'pharmacy', 'Fire station', 'fire-station', 'Police station', 'police', 'Military base', 'police', 'Train station', 'train', 'Airport', 'plane', 'Government office', 'office', 'Courthouse', 'office', 'City hall', 'office', 'Communication tower', 'communication-tower', 'Water reservoir', 'reservoir', 'Water plant', 'droplet', 'Wastewater plant', 'droplet', 'dummy-transparent'];

            map.current.addLayer({
                id: 'impact-nodes',
                type: 'symbol',
                source: 'network-tiles',
                'source-layer': sourceLayerName,
                filter: ['all',['==', '$type', 'Point'],
                    ['!=', 'type', 'road'],
                    ['!=', 'type', 'Road'],
                    ['!=', 'name', 'Residential area'],
                    ['any',
                        ['all',['has', unmetProp], ['>', unmetProp, 0]],
                        ['all', ['has', restrictedProp],['>', restrictedProp, 0]]
                    ]
                ],
                layout: {
                    'icon-image': [
                        'concat', 
                        ['match', ['get', 'type'], ...iconMatchList], 
                        '-', 
                        ['coalesce', 
                            ['get', restrictedSevProp], 
                            ['get', unmetSevProp],
                            'low'
                        ]
                    ],
                    'icon-size': 1.1,
                    'icon-allow-overlap': true,
                },
                minzoom: 0
            });

            addPopupEventListeners();
        } catch (error) {
            console.error("[MapViewImpact] Error adding layers:", error);
        }
    },[system, theme.palette.mode, getTileUrl, getSourceLayerName, floodData, getFloodRiskColor, isTransport, getPaintExpressions, addPopupEventListeners, scenarioId]);

    const setupIconsAndLayers = useCallback(async () => {
        if (!map.current || setupInProgress.current) return;
        setupInProgress.current = true;

        try {
            await new Promise(resolve => {
                if (map.current.isStyleLoaded()) return resolve();
                map.current.once('style.load', resolve);
            });

            const iconPromises =[];
            if (typeof icons === 'object' && icons !== null) {
                for (const [name, url] of Object.entries(icons)) {
                    if (name !== 'dummy-transparent') iconPromises.push(addIconToMap(url, name));
                }
            }
            const severityLevels = ['extreme', 'severe', 'high', 'medium', 'low'];
            if (typeof icons === 'object' && icons !== null) {
                for (const [iconKey, sourceUrl] of Object.entries(icons)) {
                    const baseName = iconKey.replace('-dark', '');
                    if (iconKey.endsWith('-dark') || iconKey === 'dummy-transparent' || !sourceUrl) continue;
                    for (const severity of severityLevels) {
                        const color = getSeverityColor(severity);
                        const newIconId = `${baseName}-${severity}`;
                        iconPromises.push(addIconToMap(sourceUrl, newIconId, color));
                    }
                }
            }
            
            await Promise.allSettled(iconPromises);
            
            if (isMounted.current) {
                addLayers();
            }
            
        } catch (error) {
            console.error("[MapViewImpact] Critical failure during setup:", error);
        } finally {
            if (isMounted.current) {
                setupInProgress.current = false;
            }
        }
    },[addIconToMap, getSeverityColor, addLayers]);

    // MAP INITIALIZATION
    useEffect(() => {
        mapboxgl.accessToken = 'pk.eyJ1IjoibXZwYXNzb3MiLCJhIjoiY2t6em4xcnpwMGJ2bDNicDYyemM0bWU4YyJ9.fR55BgEafulrz4yLfY2oRA';
        
        isMounted.current = true;
        if (map.current) return;
        
        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: theme.palette.mode === 'dark' ? 'mapbox://styles/mapbox/dark-v10' : 'mapbox://styles/mapbox/light-v10',
            center: [18.0656, 59.3327],
            zoom: 12,
            preserveDrawingBuffer: true
        });
        
        const handleMapLoad = () => setIsMapLoaded(true);
        const handleStyleLoad = () => setIsStyleReady(true);
        
        map.current.on('load', handleMapLoad);
        map.current.on('style.load', handleStyleLoad);
        
        const mapRef = map.current;
        
        return () => {
            isMounted.current = false;
            if (mapRef) {
                mapRef.off('load', handleMapLoad);
                mapRef.off('style.load', handleStyleLoad);
                mapRef.remove();
            }
            map.current = null;
            setupInProgress.current = false;
        };
    }, [theme.palette.mode]);

    // LAYER AND ICON SETUP
    useEffect(() => {
        if (isMapLoaded && isStyleReady && map.current && !setupInProgress.current) {
            setupIconsAndLayers();
        }
    },[isMapLoaded, isStyleReady, setupIconsAndLayers, system, scenarioId]);

    // DYNAMIC METRIC UPDATER
    useEffect(() => {
        if (!map.current || !map.current.getLayer('impact-lines')) return;
        
        const activeMetric = isTransport ? displayMetric : 'flow';
        const { colorExp, opacityExp } = getPaintExpressions(activeMetric, system);

        map.current.setPaintProperty('impact-lines', 'line-color', colorExp);
        map.current.setPaintProperty('impact-lines', 'line-opacity', opacityExp);
    }, [displayMetric, system, isTransport, getPaintExpressions]);

    // HAZARD VISIBILITY TOGGLE UPDATER
    useEffect(() => {
        if (!map.current) return;
        const hazardLayers = ['hazard-fill', 'hazard-outline', 'flood-areas'];
        hazardLayers.forEach(layer => {
            if (map.current.getLayer(layer)) {
                map.current.setLayoutProperty(layer, 'visibility', showHazard ? 'visible' : 'none');
            }
        });
    }, [showHazard]);

    // CLICK-TO-ZOOM HANDLER
    useEffect(() => {
        if (isMapLoaded && isStyleReady && map.current && map.current.isStyleLoaded() && selectedFeature && selectedFeature.coords) {
            const { coords, properties } = selectedFeature;
            map.current.flyTo({ center: coords, zoom: 16, essential: true });

            if (!popup.current) {
                popup.current = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, className: 'custom-popup' });
            }
            popup.current.setLngLat(coords).setHTML(createImpactPopupContent(properties)).addTo(map.current);
        } else if (popup.current && !selectedFeature) {
            popup.current.remove();
        }
    }, [selectedFeature, isMapLoaded, isStyleReady, createImpactPopupContent]);

    const exportMapAsPng = useCallback(() => {
        if (!map.current) return;
        try {
            const canvas = map.current.getCanvas();
            const dataUrl = canvas.toDataURL('image/png');
            const link = document.createElement('a');
            link.href = dataUrl;
            link.download = `map-impact-${system}-${Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error exporting map as PNG:', error);
            alert('Failed to export map. Please try again.');
        }
    }, [system]);

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%', borderRadius: 'inherit' }}>
            <div ref={mapContainer} style={{ width: '100%', height: '100%', borderRadius: 'inherit' }} />
            
            <MapLegend 
                getSeverityColor={getSeverityColor} 
                displayMetric={displayMetric} 
                isTransport={isTransport} 
            />

            {/* Overlays Configuration Box (Top Left) */}
            <Box position="absolute" top={16} left={16} zIndex={1} display="flex" flexDirection="column" gap={2}>
                {/* Hazard Area Toggle */}
                <Paper sx={{ px: 2, py: 1, backgroundColor: theme.palette.mode === 'dark' ? 'rgba(33, 33, 33, 0.9)' : 'rgba(255, 255, 255, 0.9)', borderRadius: '8px' }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showHazard}
                                onChange={(e) => setShowHazard(e.target.checked)}
                                size="small"
                                color="primary"
                            />
                        }
                        label={<Typography variant="body2" fontWeight="bold">Hazard Overlay</Typography>}
                        sx={{ m: 0 }}
                    />
                </Paper>

                {/* Edge Display Metric Toggle (for transport networks) */}
                {isTransport && (
                    <Paper sx={{ p: 1, backgroundColor: theme.palette.mode === 'dark' ? 'rgba(33, 33, 33, 0.9)' : 'rgba(255, 255, 255, 0.9)', borderRadius: '8px' }}>
                        <Typography variant="caption" display="block" sx={{ mb: 1, fontWeight: 'bold', px: 1 }}>
                            Network Metric Display
                        </Typography>
                        <ToggleButtonGroup
                            value={displayMetric}
                            exclusive
                            onChange={(e, newVal) => { if (newVal !== null) setDisplayMetric(newVal); }}
                            size="small"
                            orientation="vertical"
                            fullWidth
                        >
                            <ToggleButton value="flow" aria-label="Flow Volume" sx={{ textTransform: 'none', py: 0.5 }}>
                                Flow Volume
                            </ToggleButton>
                            <ToggleButton value="vc_ratio" aria-label="Congestion V/C" sx={{ textTransform: 'none', py: 0.5 }}>
                                Congestion (V/C)
                            </ToggleButton>
                            <ToggleButton value="delay_min" aria-label="Travel Delay" sx={{ textTransform: 'none', py: 0.5 }}>
                                Travel Delay
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Paper>
                )}
            </Box>

            {/* Bottom Right Export Button */}
            <Box position="absolute" bottom={16} right={16} zIndex={1}>
                <Fab color="primary" aria-label="export" onClick={exportMapAsPng} size="small">
                    <DownloadIcon />
                </Fab>
            </Box>
        </div>
    );
};

export default MapViewImpact;